import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import ExperimentWorkspaceTrackingImplementation from '@wetransfer/transfer/constant/experiments/workspace-tracking-implementation';
import { getTransferPriceAsFloat } from '@wetransfer/transfer/helper/paidTransfers';
import PreviewHelper from '@wetransfer/transfer/helper/preview';
import {
  getTotalFileSize,
  getTransferFileById,
  getTransferNumberOfFiles,
} from '@wetransfer/transfer/helper/transferFiles';
import { getTransferType } from '@wetransfer/transfer/helper/transferType';
import Analytics from '@wetransfer/transfer/modules/analytics';
import { trackAmplitudeEvent } from '@wetransfer/transfer/modules/analytics/vendors/amplitude';
import {
  getDownloadFullEvent,
  getDownloadPartialEvent,
} from '@wetransfer/transfer/modules/analytics/vendors/utils/trackDownload';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import I18n from '@wetransfer/transfer/modules/i18n';
import Router from '@wetransfer/transfer/modules/router';
import Store from '@wetransfer/transfer/store';
import { selectTransferAmplitudeEventWallpaperProperties } from '@wetransfer/transfer/store/selector/analytics';
import { TRANSFER_STATES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-states';
import {
  itemHeight,
  getItemType,
  isPreviewable,
} from '@wetransfer/transfer/transfer-bundle/helpers/preview';
import { TransferDownloadActions } from '@wetransfer/transfer/transfer-bundle/store/download-actions';
import { getCurrentSize } from '@wetransfer/transfer/transfer-bundle/store/selectors';

import GridBar from './grid-bar';
import GridItem from './grid-item';

export default class Grid extends React.Component {
  static propTypes = {
    route: PropTypes.object,
    setReturnPath: PropTypes.func,
    page: PropTypes.object,
  };

  state = {
    previewTracked: false,
  };

  constructor(props) {
    super(props);

    this.renderItems = this.renderItems.bind(this);
  }

  componentDidMount() {
    const transfer = Store.getState().transfer;

    if (transfer.state === TRANSFER_STATES.PASSWORD_PROTECTED) {
      const downloadUrl = this.props.route.path.split('/grid')[0];
      Router.navigateTo(downloadUrl);
    }
  }

  componentDidUpdate() {
    if (!Browser.isMobile) {
      const downloadUrl = this.props.route.path.split('/grid')[0];
      Router.setLastRoute('transfer', downloadUrl);
    }

    const state = Store.getState();

    const {
      transfer: {
        itemsTree: { items },
        items: transferItems,
        id,
        type,
      },
    } = state;

    if (id && !this.state.previewTracked) {
      this.setState({
        previewTracked: true,
      });

      // Files only on top level shown on the grid preview page
      const fileItems = items.filter((item) => item.uiType !== 'directory');

      const fileItemsIds = fileItems.reduce((map, item) => {
        map[item.id] = true;

        return map;
      }, {});

      // we want to count the size of all files (including folders)
      const size = getCurrentSize(state);

      Analytics.trackUnstructSnowplowEvent(
        'iglu:com.wetransfer/preview_event/jsonschema/2-0-0',
        {
          transfer_id: id,
          transfer_type: type.toUpperCase(),
          number_thumbnails: fileItems.filter((item) =>
            isPreviewable({ items: [item] })
          ).length,
          thumbnail_dimension: 0, // intentional! (for now)
          number_files: transferItems.length,
          number_folders: items.length - fileItems.length,
          files_total_size: size,
          files: transferItems.map((item) => ({
            id: item.id,
            extension: item.file.type,
            size: item.size,
            // we want to track previewable files on top level only
            previewable:
              isPreviewable({ items: [item] }) &&
              Boolean(fileItemsIds[item.id]),
          })),
        }
      );
    }
  }

  shuffleItems(items) {
    // Total height of left column, sum of all images' height
    let leftTotalHeight = 0;
    // Total height of right column, sum of all images' height
    let rightTotalHeight = 0;

    // Items rendered on the left column
    const leftItems = [];
    // Items rendered on the right column
    const rightItems = [];

    items.forEach((item) => {
      const { type, metadata } = item;
      const nodeDimensions = { height: 80, width: 400 };

      const height = itemHeight[type]
        ? itemHeight[type](nodeDimensions, metadata)
        : 80;

      if (rightTotalHeight - leftTotalHeight < 0) {
        rightItems.push(item);
        rightTotalHeight += height;
      } else {
        leftItems.push(item);
        leftTotalHeight += height;
      }
    });

    return {
      leftItems,
      rightItems,
    };
  }

  get scrollElement() {
    return (
      this.props.page &&
      this.props.page.scrollableNode &&
      this.props.page.scrollableNode.contentNode
    );
  }

  onItemClick = (item) => {
    if (Browser.isMobile) {
      return;
    }

    const {
      transfer: { id, recipient_id, secret },
    } = Store.getState();

    Router.navigateTo(
      PreviewHelper.getPreviewUrl({
        transferId: id,
        fileId: item.id,
        secret,
        recipientId: recipient_id,
      })
    );

    if (getItemType(item.file) === 'image') {
      Analytics.trackSnowplowEvent({
        category: 'preview',
        action: 'click_item_preview',
      });
    }

    this.props.setReturnPath(
      this.props.route.path,
      I18n.t('transfer.preview.overview')
    );
  };

  onDownloadAll = async () => {
    const state = Store.getState();
    const { transfer, session } = state;
    const currentSize = getCurrentSize(state);

    if (transfer.currentFileDownload) {
      return;
    }

    this.trackDownloadEvent({ action: 'click_download_all', label: 'grid' });

    const {
      wallpaperNumber,
      wallpaperType,
      wallpaperFileType,
      wallpaperSource,
    } = selectTransferAmplitudeEventWallpaperProperties(Store.getState());

    const transferType = getTransferType(transfer);
    const transferPrice = getTransferPriceAsFloat(transfer);
    const transferSize = getTotalFileSize(transfer);
    const numberOfFiles = getTransferNumberOfFiles(transfer);

    Analytics.trackAmplitude(
      getDownloadFullEvent(
        transferType,
        numberOfFiles,
        transfer.id,
        ExperimentWorkspaceTrackingImplementation.isWorkspaceTracking()
          ? transferSize
          : undefined,
        wallpaperNumber,
        wallpaperType,
        wallpaperFileType,
        wallpaperSource,
        transferPrice
      )
    );

    const { params } = this.props.route;

    await Store.dispatch(
      TransferDownloadActions.downloadTransfer({
        transfer,
        secret: params.secret,
        recipientId: params.recipient_id || false,
        domainUserId: session.domainUserId,
        currentSize,
      })
    );
  };

  onDownload = (item) => {
    let params = {};
    const { transfer, session } = Store.getState();

    if (this.props.route) {
      params = this.props.route.params;
    } else {
      params = Store.getState().route.params;
    }

    this.trackDownloadEvent({ action: 'click_download_file', label: 'grid' });

    const {
      wallpaperNumber,
      wallpaperType,
      wallpaperFileType,
      wallpaperSource,
    } = selectTransferAmplitudeEventWallpaperProperties(Store.getState());

    const transferType = getTransferType(transfer);
    const transferPrice = getTransferPriceAsFloat(transfer);
    const fileSize = getTransferFileById(transfer, item.id)?.size;

    trackAmplitudeEvent(
      getDownloadPartialEvent(
        transferType,
        transfer.id,
        ExperimentWorkspaceTrackingImplementation.isWorkspaceTracking()
          ? fileSize
          : undefined,
        wallpaperNumber,
        wallpaperType,
        wallpaperFileType,
        wallpaperSource,
        transferPrice
      )
    );

    Store.dispatch(
      TransferDownloadActions.downloadTransferFile({
        transfer,
        secret: params.secret,
        recipientId: params.recipient_id,
        fileId: item.id,
        domainUserId: session.domainUserId,
      })
    );
  };

  trackDownloadEvent = ({ action, label }) => {
    Analytics.trackSnowplowEvent({
      category: 'download',
      action,
      label,
    });
  };

  renderItems(items) {
    const shuffledItems = this.shuffleItems(items);

    return (
      <div className="grid__layout">
        <div className="row">
          <div className="col">
            {shuffledItems.leftItems.map((item) => (
              <GridItem
                item={item}
                key={item.id}
                onDownload={this.onDownload}
                onItemClick={this.onItemClick}
                scrollElement={this.scrollElement}
              />
            ))}
          </div>
          <div className="col">
            {shuffledItems.rightItems.map((item) => (
              <GridItem
                item={item}
                key={item.id}
                onDownload={this.onDownload}
                onItemClick={this.onItemClick}
                scrollElement={this.scrollElement}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const state = Store.getState();
    const { transfer } = state;
    const items = transfer.itemsTree.items;
    /*
    Items number is taken from `transfer.items`
    even though items themselves come from `transfer.itemsTree.items`.
    The reason for that is to keep consistency with Transfer/Downloader.
    So, while displaying only top level items,
    the total number is taken from the flat list.
    */
    const itemsNumber = transfer.items.length;

    if (!items) {
      return null;
    }

    const title = I18n.t('transfer.preview.grid');
    let expires = I18n.t('transfer.download.expires', {
      relative_time: I18n.relativeTime(transfer.expiryInSeconds),
    });

    if (typeof transfer.expiryInSeconds === 'undefined') {
      expires = I18n.t('transfer.download.expires_never');
    }

    const description = I18n.t('transfer.download.format', {
      expires,
      files: I18n.t('transfer.download.files', {
        count: itemsNumber,
        size: I18n.size(getCurrentSize(state) || 0, { precision: 0 }),
      }),
    });

    return (
      <div
        className={classnames('grid-container', {
          'grid-container--receiver-UI': Browser.isMobile,
        })}
      >
        <GridBar
          title={title}
          description={description}
          onDownloadAll={this.onDownloadAll}
        />
        {this.renderItems(items)}
      </div>
    );
  }
}
